

// // ============================================================================
// // PROMO CODES FUNCTIONS - SECURE CLIENT-SIDE INTERFACE
// // ============================================================================

import {
    getFirestore,
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit,
} from "firebase/firestore";
import { initializeApp } from "firebase/app";
import { firebaseConfig, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

/**
 * Create a new promo code (SECURE - Server-side)
 * @param {string} storeId - The ID of the store
 * @param {object} promoCodeData - Promo code configuration
 * @returns {Promise<string>} The created promo code ID
 */
export const createPromoCode = async (storeId, promoCodeData) => {
    try {
        if (!storeId) {
            throw new Error("Store ID is required");
        }

        // Call the secure Cloud Function
        const createPromoCodeFunction = httpsCallable(functions, 'createPromoCode');
        const result = await createPromoCodeFunction({
            storeId: storeId,
            promoCodeData: promoCodeData
        });

        if (result.data.success) {
            return result.data.promoCodeId;
        } else {
            throw new Error(result.data.message || 'Failed to create promo code');
        }
    } catch (error) {
        console.error('Error creating promo code:', error);
        throw new Error(error.message || 'Failed to create promo code');
    }
};

/**
 * Get all promo codes for a store
 * @param {string} storeId - The ID of the store
 * @param {boolean} activeOnly - Whether to return only active promo codes
 * @returns {Promise<Array>} Array of promo codes
 */
export const getPromoCodes = async (storeId, activeOnly = false) => {
    try {
        let q = query(
            collection(db, "stores", storeId, "promoCodes"),
            orderBy("createdAt", "desc")
        );

        if (activeOnly) {
            q = query(
                collection(db, "stores", storeId, "promoCodes"),
                where("isActive", "==", true),
                orderBy("createdAt", "desc")
            );
        }

        const querySnapshot = await getDocs(q);
        const promoCodes = [];

        querySnapshot.forEach((doc) => {
            const data = doc.data();
            promoCodes.push({
                id: doc.id,
                ...data,
                // Convert Firestore timestamps to Date objects
                validFrom: data.validFrom?.toDate ? data.validFrom.toDate() : data.validFrom,
                validUntil: data.validUntil?.toDate ? data.validUntil.toDate() : data.validUntil,
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
                updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
            });
        });

        return promoCodes;
    } catch (error) {
        console.error("Error getting promo codes:", error);
        throw error;
    }
};

/**
 * Get a specific promo code by code string
 * @param {string} storeId - The ID of the store
 * @param {string} code - The promo code string
 * @returns {Promise<object|null>} Promo code data or null if not found
 */
export const getPromoCodeByCode = async (storeId, code) => {
    try {
        const upperCode = String(code).toUpperCase().trim();
        const q = query(
            collection(db, "stores", storeId, "promoCodes"),
            where("code", "==", upperCode),
            limit(1)
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        const doc = querySnapshot.docs[0];
        const data = doc.data();

        return {
            id: doc.id,
            ...data,
            validFrom: data.validFrom?.toDate ? data.validFrom.toDate() : data.validFrom,
            validUntil: data.validUntil?.toDate ? data.validUntil.toDate() : data.validUntil,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        };
    } catch (error) {
        console.error("Error getting promo code by code:", error);
        throw error;
    }
};

/**
 * Update a promo code (SECURE - Server-side)
 * @param {string} storeId - The ID of the store
 * @param {string} promoCodeId - The promo code document ID
 * @param {object} updateData - Data to update
 * @returns {Promise<boolean>} Success status
 */
export const updatePromoCode = async (storeId, promoCodeId, updateData) => {
    try {
        if (!storeId || !promoCodeId) {
            throw new Error("Store ID and promo code ID are required");
        }

        // Call the secure Cloud Function
        const updatePromoCodeFunction = httpsCallable(functions, 'updatePromoCode');
        const result = await updatePromoCodeFunction({
            storeId: storeId,
            promoCodeId: promoCodeId,
            promoCodeData: updateData
        });

        if (result.data.success) {
            return true;
        } else {
            throw new Error(result.data.message || 'Failed to update promo code');
        }
    } catch (error) {
        console.error('Error updating promo code:', error);
        throw new Error(error.message || 'Failed to update promo code');
    }
};

/**
 * Delete a promo code (SECURE - Server-side)
 * @param {string} storeId - The ID of the store
 * @param {string} promoCodeId - The promo code document ID
 * @returns {Promise<boolean>} Success status
 */
export const deletePromoCode = async (storeId, promoCodeId) => {
    try {
        if (!storeId || !promoCodeId) {
            throw new Error("Store ID and promo code ID are required");
        }

        // Call the secure Cloud Function
        const deletePromoCodeFunction = httpsCallable(functions, 'deletePromoCode');
        const result = await deletePromoCodeFunction({
            storeId: storeId,
            promoCodeId: promoCodeId
        });

        if (result.data.success) {
            return true;
        } else {
            throw new Error(result.data.message || 'Failed to delete promo code');
        }
    } catch (error) {
        console.error('Error deleting promo code:', error);
        throw new Error(error.message || 'Failed to delete promo code');
    }
};

/**
 * Toggle promo code active status
 * @param {string} storeId - The ID of the store
 * @param {string} promoCodeId - The promo code document ID
 * @param {boolean} isActive - New active status
 * @returns {Promise<boolean>} Success status
 */
export const togglePromoCodeStatus = async (storeId, promoCodeId, isActive) => {
    try {
        return await updatePromoCode(storeId, promoCodeId, { isActive });
    } catch (error) {
        console.error("Error toggling promo code status:", error);
        throw error;
    }
};


/**
 * Get active promo codes for display (e.g., in a promo codes list)
 * @param {string} storeId - The ID of the store
 * @param {boolean} publicOnly - Whether to return only public promo codes
 * @returns {Promise<Array>} Array of active promo codes
 */
export const getActivePromoCodes = async (storeId, publicOnly = true) => {
    try {
        const now = new Date();

        let q = query(
            collection(db, "stores", storeId, "promoCodes"),
            where("isActive", "==", true),
            orderBy("createdAt", "desc")
        );

        const querySnapshot = await getDocs(q);
        const promoCodes = [];

        querySnapshot.forEach((doc) => {
            const data = doc.data();

            // Check if promo code is currently valid
            const validFrom = data.validFrom?.toDate ? data.validFrom.toDate() : data.validFrom;
            const validUntil = data.validUntil?.toDate ? data.validUntil.toDate() : data.validUntil;

            const isCurrentlyValid = (!validFrom || validFrom <= now) &&
                (!validUntil || validUntil >= now);

            const hasUsageLeft = data.usageLimit === 0 || data.usedCount < data.usageLimit;

            if (isCurrentlyValid && hasUsageLeft) {
                promoCodes.push({
                    id: doc.id,
                    ...data,
                    validFrom,
                    validUntil,
                    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
                    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
                });
            }
        });

        return promoCodes;
    } catch (error) {
        console.error("Error getting active promo codes:", error);
        throw error;
    }
};

/**
 * Get promo code usage statistics
 * @param {string} storeId - The ID of the store
 * @param {string} promoCodeId - The promo code ID
 * @returns {Promise<object>} Usage statistics
 */
export const getPromoCodeStats = async (storeId, promoCodeId) => {
    try {
        // Get the promo code
        const promoCodeRef = doc(db, "stores", storeId, "promoCodes", promoCodeId);
        const promoCodeDoc = await getDoc(promoCodeRef);

        if (!promoCodeDoc.exists()) {
            throw new Error("Promo code not found");
        }

        const promoCodeData = promoCodeDoc.data();

        // Get orders that used this promo code
        const ordersQuery = query(
            collection(db, "stores", storeId, "orders"),
            where("promoCode.promoCodeId", "==", promoCodeId)
        );

        const ordersSnapshot = await getDocs(ordersQuery);

        let totalRevenue = 0;
        let totalDiscount = 0;
        const orderIds = [];

        ordersSnapshot.forEach((doc) => {
            const orderData = doc.data();
            totalRevenue += orderData.total || 0;
            totalDiscount += orderData.discountAmount || 0;
            orderIds.push(doc.id);
        });

        return {
            promoCode: {
                id: promoCodeDoc.id,
                ...promoCodeData
            },
            stats: {
                totalUsages: ordersSnapshot.size,
                totalRevenue,
                totalDiscount,
                averageOrderValue: ordersSnapshot.size > 0 ? totalRevenue / ordersSnapshot.size : 0,
                conversionValue: totalRevenue - totalDiscount,
                orderIds
            }
        };
    } catch (error) {
        console.error("Error getting promo code stats:", error);
        throw error;
    }
};


/**
 * Get store promo settings for client-side display
 * @param {string} storeId - The ID of the store
 * @returns {Promise<object>} Promo settings data
 */
export const getPromoSettings = async (storeId) => {
    try {
        const storeDoc = await getDoc(doc(db, "stores", storeId));

        if (!storeDoc.exists()) {
            throw new Error("Store not found");
        }

        const data = storeDoc.data();
        return data.promoSettings || {
            enabled: false,
            title: "",
            description: "",
            backgroundColor: "#ff6b6b",
            textColor: "#ffffff",
            buttonText: "Shop Now",
            buttonLink: "",
            showButton: true,
            displayDuration: 0,
            position: "top"
        };
    } catch (error) {
        console.error("Error getting promo settings:", error);
        throw error;
    }
};


